import React, { useState, useEffect, useCallback } from 'react';
import { dashboardAPI } from '../services/api';
import DateFilter from '../components/DateFilter';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line } from 'recharts';

const COLORS = ['#1a56db', '#059669', '#d97706', '#dc2626'];
const fmt = (v) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(v);

export default function DashboardPage() {
  const [filters, setFilters] = useState({});
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [modules, setModules] = useState([]);
  const [topPassengers, setTopPassengers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const [s, t, m, p] = await Promise.all([
        dashboardAPI.summary(params),
        dashboardAPI.trend(params),
        dashboardAPI.modules(params),
        dashboardAPI.topPassengers({ ...params, limit: 10 }),
      ]);
      setSummary(s.data);
      setTrend(t.data);
      setModules(m.data);
      setTopPassengers(p.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleFilter = (params) => {
    setFilters(params);
    load(params);
  };

  if (loading && !summary) return <div className="loading">Yükleniyor...</div>;

  return (
    <div>
      <div className="topbar">
        <h1>Genel Bakış</h1>
        <DateFilter onFilter={handleFilter} />
      </div>

      {summary && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="label">Uçak Bileti</div>
            <div className="value">{summary.flights.count}</div>
            <div className="sub">{fmt(summary.flights.total)}</div>
          </div>
          <div className="stat-card">
            <div className="label">Otel Konaklama</div>
            <div className="value">{summary.hotels.count}</div>
            <div className="sub">{fmt(summary.hotels.total)}</div>
          </div>
          <div className="stat-card">
            <div className="label">Transfer</div>
            <div className="value">{summary.transfers.count}</div>
            <div className="sub">{fmt(summary.transfers.total)}</div>
          </div>
          <div className="stat-card">
            <div className="label">Araç Kiralama</div>
            <div className="value">{summary.carrentals.count}</div>
            <div className="sub">{fmt(summary.carrentals.total)}</div>
          </div>
          <div className="stat-card">
            <div className="label">Toplam Harcama</div>
            <div className="value" style={{ color: 'var(--primary)' }}>{fmt(summary.grand_total)}</div>
          </div>
          <div className="stat-card">
            <div className="label">Yolcu Sayısı</div>
            <div className="value">{summary.passengers}</div>
          </div>
        </div>
      )}

      <div className="charts-grid">
        <div className="chart-card">
          <h3>Aylık Harcama Trendi</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `${(v/1000).toFixed(0)}K`} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Legend />
              <Line type="monotone" dataKey="flight" name="Uçak" stroke="#1a56db" strokeWidth={2} />
              <Line type="monotone" dataKey="hotel" name="Otel" stroke="#059669" strokeWidth={2} />
              <Line type="monotone" dataKey="transfer" name="Transfer" stroke="#d97706" strokeWidth={2} />
              <Line type="monotone" dataKey="carrental" name="Araç" stroke="#dc2626" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h3>Hizmet Dağılımı</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={modules.filter(m => m.value > 0)} dataKey="value" nameKey="name" cx="50%" cy="50%"
                outerRadius={100} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                {modules.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmt(v)} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {topPassengers.length > 0 && (
        <div className="chart-card">
          <h3>En Çok Harcama Yapan Yolcular</h3>
          <ResponsiveContainer width="100%" height={Math.max(300, topPassengers.length * 40)}>
            <BarChart data={topPassengers} layout="vertical" margin={{ left: 120 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tickFormatter={v => `${(v/1000).toFixed(0)}K`} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={110} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Legend />
              <Bar dataKey="flight_total" name="Uçak" stackId="a" fill="#1a56db" />
              <Bar dataKey="hotel_total" name="Otel" stackId="a" fill="#059669" />
              <Bar dataKey="transfer_total" name="Transfer" stackId="a" fill="#d97706" />
              <Bar dataKey="car_total" name="Araç" stackId="a" fill="#dc2626" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
